import { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faBolt, faCircleExclamation, faCheck, faRotate, faPlus, faLock,
} from "@fortawesome/free-solid-svg-icons";
import { TextField, MenuItem, Box } from "@mui/material";
import InfoTooltip from "./InfoTooltip.js";
import { navigateExternal } from "../utils/navigation.js";
import { canWrite } from "../utils/permissions";
import "./MessageCredits.css";

const BASE = process.env.REACT_APP_API_BASE_URL;
const fmt = (n) => Number(n || 0).toLocaleString("de-CH");

// Shown when a Free / non-paid agent interacts with a locked control.
const LOCK_MSG =
  "Upgrade this agent to a paid plan first to buy extra messages.";
const LOCK_MSG_CREDITS =
  "Upgrade this agent to a paid plan first to buy extra AI credits.";

// MUI field styling matched to the Account Settings page (Administration.js):
// neutral focus border + brand-purple focused label, brand-purple selected menu
// item with a light-blue hover.
const MC_TF_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
  "& .MuiOutlinedInput-root.Mui-focused": { "& fieldset": { border: "1px solid #515151" } },
  "& .MuiInputLabel-root": { color: "gray", "&.Mui-focused": { color: "#5616ea" } },
};
const MC_MENU_SX = {
  backgroundColor: "#ffffff !important",
  "&:hover": { backgroundColor: "#bbe4f6 !important", color: "black !important" },
  "&.Mui-selected": { backgroundColor: "#5616ea !important", color: "white !important" },
};

// Shared data + actions for the message-credit UI. Lifted into a hook so the
// usage meter (rendered at the TOP of the billing page) and the buy/auto-reload
// panel (rendered BELOW the plan tiers) share a single fetch.
export function useMessageCredits(botId, onChanged) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // action key in flight

  // Auto-reload form draft (seeded from the saved settings once loaded).
  const [arPackId, setArPackId] = useState(null);
  const [arThreshold, setArThreshold] = useState(50);
  const seededRef = useRef(false);

  const load = useCallback(async () => {
    if (!botId) return null;
    try {
      const res = await fetch(`${BASE}bots/${botId}/message-credits`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
      return json;
    } catch (e) {
      setError("Could not load message-credit details.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    seededRef.current = false;
    setLoading(true);
    load();
  }, [load]);

  // Seed the auto-reload form once from the saved settings.
  useEffect(() => {
    if (!data || seededRef.current) return;
    seededRef.current = true;
    const ar = data.autoreload || {};
    setArThreshold(ar.threshold ?? 50);
    setArPackId(ar.pack_id ?? (data.packs?.[0]?.id ?? null));
  }, [data]);

  // Returning from Stripe (?credits=1 / ?autoreload=1): credits land via
  // webhook, which can lag the redirect by a moment, so poll briefly until the
  // status reflects the purchase, then strip the one-shot query params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("credits") && !params.get("autoreload")) return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      await load();
      if (onChanged) onChanged();
      if (!cancelled && tries < 6) setTimeout(tick, 2500);
    };
    tick();
    window.history.replaceState({}, "", `${window.location.origin}${window.location.pathname}`);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const denyIfReadOnly = useCallback(() => {
    if (!canWrite()) {
      window.dispatchEvent(new CustomEvent("ub:permission_denied", { detail: { message: "" } }));
      return true;
    }
    return false;
  }, []);

  const successUrl = () => `${window.location.origin}${window.location.pathname}`;

  const buyPack = useCallback(async (pack) => {
    if (denyIfReadOnly()) return;
    setPending(`buy:${pack.id}`);
    setError(null);
    try {
      const res = await fetch(`${BASE}message-credits/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_id: botId, pack_id: pack.id, success_url: successUrl() }),
      });
      const json = await res.json();
      if (json?.checkout_url) navigateExternal(json.checkout_url);
      else { setError("Could not start checkout. Please try again."); setPending(null); }
    } catch (e) {
      setError("Could not start checkout. Please try again.");
      setPending(null);
    }
  }, [botId, denyIfReadOnly]);

  const saveAutoReload = useCallback(async (enable) => {
    if (denyIfReadOnly()) return;
    setPending("autoreload");
    setError(null);
    try {
      const res = await fetch(`${BASE}message-credits/autoreload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot_id: botId,
          enabled: enable,
          pack_id: arPackId,
          threshold: Number(arThreshold) || 0,
          success_url: successUrl(),
        }),
      });
      const json = await res.json();
      if (json?.needs_setup && json?.checkout_url) {
        navigateExternal(json.checkout_url); // collect a card, webhook activates
        return;
      }
      await load();
      if (onChanged) onChanged();
      setPending(null);
    } catch (e) {
      setError("Could not update automatic top-up. Please try again.");
      setPending(null);
    }
  }, [botId, arPackId, arThreshold, denyIfReadOnly, load, onChanged]);

  return {
    data, loading, error, pending,
    arPackId, setArPackId, arThreshold, setArThreshold,
    buyPack, saveAutoReload,
  };
}

// Usage meter — rendered at the TOP of the billing page (above the plan
// monthly/yearly toggle). Hidden when there's no real allowance (e.g. a Free
// plan with a 0 message_limit) to avoid an "X / 0" meter.
export function MessageCreditsMeter({ credits }) {
  const { data, loading } = credits;
  // Reserve the meter's spot with the app's standard spinner while the status
  // loads, so it doesn't pop in blank-then-filled.
  if (loading) {
    return (
      <div className="mc-meter mc-meter-top-block mc-meter-loading" aria-busy="true">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }
  if (!data) return null;
  const used = Number(data.current_message_count || 0);
  const total = Number(data.total_allowance || 0);
  if (total <= 0) return null;
  const purchased = Number(data.purchased_remaining || 0);
  const planLimit = Number(data.plan_message_limit || 0);
  const pct = Math.min(100, Math.round((used / total) * 100));
  const nearLimit = used >= total - 10;
  // AI credits on (backend controller/credits): the same figures, in AI credits
  const inCredits = data.unit === "credits";

  return (
    <>
      <div className="mc-meter mc-meter-top-block">
        <div className="mc-meter-top">
          <span className="mc-meter-label">{inCredits ? "AI credits this period" : "AI responses this month"}</span>
          <span className="mc-meter-count">{fmt(used)} / {fmt(total)}</span>
        </div>
        <div className="mc-track">
          <div className={`mc-fill${nearLimit ? " near" : ""}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mc-meter-foot">
          <span>{fmt(planLimit)} from plan{purchased > 0 ? ` + ${fmt(purchased)} ${inCredits ? "extra" : "purchased"}` : ""}</span>
          <span>{fmt(data.remaining)} remaining</span>
        </div>
      </div>
      {/* Thin grey divider below the usage meter, above the plan toggle —
          matches the dividers between the page's other sections. */}
      <div className="mc-top-divider" />
    </>
  );
}

// Buy-packs + automatic-top-up panel — rendered BELOW the plan tiers.
function MessageCredits({ credits }) {
  const {
    data, loading, error, pending,
    arPackId, setArPackId, arThreshold, setArThreshold,
    buyPack, saveAutoReload,
  } = credits;

  if (loading || !data) return null;

  // Free / non-paid agents still SEE the section (so they discover the feature)
  // but everything is locked, with an upgrade prompt.
  const locked = !data.eligible;
  const inCredits = data.unit === "credits";
  const ar = data.autoreload || {};
  const busy = pending !== null;

  // Wrap a control in the upgrade tooltip when locked. The button stays
  // interactive (not the native `disabled` attr) so hover reliably reaches the
  // tooltip wrapper and a tap/click also surfaces it; the `locked` guard makes
  // the click a no-op so nothing is purchased/enabled.
  const lockWrap = (node) =>
    locked ? <InfoTooltip text={inCredits ? LOCK_MSG_CREDITS : LOCK_MSG} placement="top">{node}</InfoTooltip> : node;

  return (
    <section className="mc-credits">
      <div className="mc-head">
        <h2 className="mc-title"><FontAwesomeIcon icon={faBolt} /> {inCredits ? "Extra AI credits" : "Extra messages"}</h2>
        {inCredits ? (
          <p className="mc-sub">
            AI credits pay for your agent&apos;s answers and for Mo. Extra credits are used after the
            credits of your plan, and stay valid for 12 months.
          </p>
        ) : (
          <p className="mc-sub">
            Add more AI responses to this agent. Purchased messages are used
            only after the monthly plan allowance runs out, and stay valid for 12 months.
          </p>
        )}
      </div>

      {locked && (
        <div className="mc-locked-banner">
          <FontAwesomeIcon icon={faLock} />
          <span>
            {inCredits
              ? "Extra AI credits are available on paid plans. Upgrade this agent to a paid plan to buy them."
              : "Message credits are available on paid plans. Upgrade this agent to a paid plan to buy extra messages."}
          </span>
        </div>
      )}

      {error && (
        <div className="mc-error">
          <FontAwesomeIcon icon={faCircleExclamation} /> {error}
        </div>
      )}

      {/* ── Buy packs ── */}
      <div className="mc-packs">
        {(data.packs || []).map((p) => (
          <div className="mc-pack" key={p.id}>
            <div className="mc-pack-credits">{fmt(p.credits)}</div>
            <div className="mc-pack-unit">{inCredits ? "AI credits" : "extra messages"}</div>
            <div className="mc-pack-price">${fmt(p.price)}</div>
            {lockWrap(
              <button
                type="button"
                className={`mc-pack-cta${locked ? " locked" : ""}`}
                disabled={busy && !locked}
                aria-disabled={locked || undefined}
                onClick={() => { if (locked) return; buyPack(p); }}
              >
                {pending === `buy:${p.id}`
                  ? <FontAwesomeIcon icon={faSpinner} spin />
                  : <><FontAwesomeIcon icon={faPlus} /> Buy</>}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* ── Auto-reload ── (temporarily hidden from users; backend stays intact) */}
      {false && (
      <div className="mc-autoreload">
        <div className="mc-ar-head">
          <div>
            <span className="mc-ar-title"><FontAwesomeIcon icon={faRotate} /> Automatic top-up</span>
            <p className="mc-ar-desc">
              Automatically buy a pack when this agent's remaining messages run low,
              so it never pauses mid-conversation.
            </p>
          </div>
          {ar.enabled && ar.status === "active" && (
            <span className="mc-ar-badge on"><FontAwesomeIcon icon={faCheck} /> On</span>
          )}
          {ar.status === "needs_action" && (
            <span className="mc-ar-badge warn"><FontAwesomeIcon icon={faCircleExclamation} /> Action needed</span>
          )}
        </div>

        {ar.status === "needs_action" && !locked && (
          <div className="mc-ar-alert">
            Your last automatic charge did not go through{ar.last_error ? ` (${ar.last_error})` : ""}.
            Re-enable below to update your card.
          </div>
        )}

        <Box className="mc-ar-controls">
          <div className="mc-ar-fields">
            <TextField
              select
              label="Buy this pack"
              value={arPackId ?? ""}
              onChange={(e) => setArPackId(Number(e.target.value))}
              disabled={busy || locked}
              variant="outlined"
              className="mc-ar-input mc-ar-input-select"
              sx={MC_TF_SX}
            >
              {(data.packs || []).map((p) => (
                <MenuItem key={p.id} value={p.id} sx={MC_MENU_SX}>
                  {fmt(p.credits)} messages for ${fmt(p.price)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="number"
              label="When remaining drops to"
              value={arThreshold}
              onChange={(e) => setArThreshold(e.target.value)}
              disabled={busy || locked}
              variant="outlined"
              className="mc-ar-input mc-ar-input-num"
              inputProps={{ min: 0 }}
              sx={MC_TF_SX}
            />
          </div>

          <div className="mc-ar-actions">
            {ar.enabled ? (
              <>
                <button
                  type="button"
                  className="mc-btn primary"
                  disabled={busy}
                  onClick={() => saveAutoReload(true)}
                >
                  {pending === "autoreload" ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save changes"}
                </button>
                <button
                  type="button"
                  className="mc-btn danger"
                  disabled={busy}
                  onClick={() => saveAutoReload(false)}
                >
                  Turn off
                </button>
              </>
            ) : (
              lockWrap(
                <button
                  type="button"
                  className={`mc-btn primary${locked ? " locked" : ""}`}
                  disabled={busy && !locked}
                  aria-disabled={locked || undefined}
                  onClick={() => { if (locked) return; saveAutoReload(true); }}
                >
                  {pending === "autoreload"
                    ? <FontAwesomeIcon icon={faSpinner} spin />
                    : (ar.has_payment_method ? "Turn on" : "Set up automatic top-up")}
                </button>
              )
            )}
          </div>
        </Box>
      </div>
      )}
    </section>
  );
}

export default MessageCredits;
